import { IdentityError } from '../network/IdentityService';
import type { IdentityService, PlayerProfile } from '../network/IdentityService';

export class PasswordRotation {
  private readonly dialog: HTMLDialogElement;
  private readonly section: HTMLElement;
  private readonly state: HTMLElement;
  private readonly toggle: HTMLButtonElement;
  private readonly fields: HTMLElement;
  private readonly currentPassword: HTMLInputElement;
  private readonly newPassword: HTMLInputElement;
  private readonly confirmation: HTMLInputElement;
  private readonly error: HTMLElement;
  private readonly submit: HTMLButtonElement;
  private ownerID = '';
  private editorOpen = false;
  private pending = false;
  private notice = '';

  public constructor(root: HTMLElement, private readonly identity: IdentityService) {
    this.dialog = required(root, '[data-account]');
    this.section = required(root, '[data-password-settings]');
    this.state = required(root, '[data-password-state]');
    this.toggle = required(root, '[data-password-toggle]');
    this.fields = required(root, '[data-password-fields]');
    this.currentPassword = required(root, '[data-password-current]');
    this.newPassword = required(root, '[data-password-new]');
    this.confirmation = required(root, '[data-password-confirm]');
    this.error = required(root, '[data-password-error]');
    this.submit = required(root, '[data-password-submit]');

    this.toggle.addEventListener('click', this.toggleEditor);
    this.submit.addEventListener('click', () => void this.rotate());
    for (const input of [this.currentPassword, this.newPassword, this.confirmation]) {
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        void this.rotate();
      });
    }
    this.dialog.addEventListener('close', () => this.reset(true));
    this.identity.subscribe(this.render);
  }

  private readonly toggleEditor = (): void => {
    if (this.pending || this.toggle.disabled) return;
    this.editorOpen = !this.editorOpen;
    this.notice = '';
    if (!this.editorOpen) this.clearFields();
    this.render(this.identity.current());
    if (this.editorOpen) this.currentPassword.focus();
  };

  private readonly render = (profile: PlayerProfile | null): void => {
    const isAccount = profile?.guest === false;
    if ((profile?.id ?? '') !== this.ownerID) {
      this.ownerID = profile?.id ?? '';
      this.reset(true);
    }
    if (!isAccount) this.editorOpen = false;
    this.section.hidden = !isAccount;
    this.fields.hidden = !this.editorOpen;
    this.state.textContent = this.notice || 'ALL SESSIONS PROTECTED';
    this.toggle.textContent = this.editorOpen ? 'CANCEL' : 'ROTATE';
    this.toggle.disabled = this.pending || !isAccount;
    this.toggle.setAttribute('aria-expanded', String(this.editorOpen));
    for (const input of [this.currentPassword, this.newPassword, this.confirmation]) input.disabled = this.pending;
    this.submit.disabled = this.pending;
  };

  private async rotate(): Promise<void> {
    if (this.pending || this.identity.current()?.guest !== false) return;
    if (this.currentPassword.value.length < 10) {
      this.showError('ENTER YOUR CURRENT PASSWORD.');
      this.currentPassword.focus();
      return;
    }
    if (this.newPassword.value.length < 10) {
      this.showError('NEW PASSWORDS USE AT LEAST 10 CHARACTERS.');
      this.newPassword.focus();
      return;
    }
    if (this.newPassword.value !== this.confirmation.value) {
      this.showError('NEW PASSWORDS DO NOT MATCH.');
      this.confirmation.focus();
      return;
    }
    if (this.newPassword.value === this.currentPassword.value) {
      this.showError('CHOOSE A DIFFERENT PASSWORD.');
      this.newPassword.focus();
      return;
    }

    this.pending = true;
    this.error.dataset.kind = '';
    this.error.textContent = 'ROTATING CREDENTIALS…';
    this.render(this.identity.current());
    try {
      await this.identity.changePassword(this.currentPassword.value, this.newPassword.value);
      for (const input of this.dialog.querySelectorAll<HTMLInputElement>('input[type="password"]')) input.value = '';
      this.clearFields();
      this.editorOpen = false;
      this.notice = 'PASSWORD ROTATED // OTHER SESSIONS CLOSED';
      this.error.textContent = '';
    } catch (error) {
      this.currentPassword.value = '';
      this.showError(error instanceof IdentityError ? error.message : 'The credential link failed.');
    } finally {
      this.pending = false;
      this.render(this.identity.current());
    }
  }

  private reset(clearNotice: boolean): void {
    if (this.pending) return;
    this.editorOpen = false;
    if (clearNotice) this.notice = '';
    this.clearFields();
    this.render(this.identity.current());
  }

  private clearFields(): void {
    this.currentPassword.value = '';
    this.newPassword.value = '';
    this.confirmation.value = '';
    this.error.textContent = '';
    this.error.dataset.kind = '';
  }

  private showError(message: string): void {
    this.error.dataset.kind = 'error';
    this.error.textContent = message.toUpperCase();
  }
}

function required<T extends Element>(root: HTMLElement, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing password rotation element: ${selector}`);
  return element;
}
